// Pricing backend tRPC client for the dashboard (PE-1572).
//
// Mirrors packages/dashboard/src/api/marketing.ts — same conventions, same
// rationale. Read that file's header first; the notes below are pricing-specific.
//
// CONVENTIONS (identical to marketing.ts):
//   - The proxy client is intentionally TYPED AS `any`, NOT `<PricingRouter>`.
//     Dashboard's tsconfig uses `moduleResolution: "node"` (legacy) which can't
//     resolve the subpath `exports` the pricing backend uses internally
//     (`@charitystack/entitlements`, `@charitystack/schemas/http/pricing`). If
//     we typed the proxy via a `PricingRouter` import, tsc would follow into
//     backends/pricing/* and surface spurious resolution errors — and it would
//     also be a `packages/* → backends/*` import, which dependency-cruiser's
//     `no-fe-importing-backend-runtime` rule forbids. So the client stays
//     untyped and callers type their ARGS via `@charitystack/schemas/http/pricing`
//     (a `contracts/*` package the dashboard may import — see dep-cruiser).
//   - Cached client at module scope.
//   - `Authorization: <token>` (NO `Bearer ` prefix) — matches getAuthToken();
//     the pricing backend's clerk-auth middleware strips an optional `Bearer `.
//   - `cache: 'no-store'` fetch override so tRPC GETs aren't HTTP-cached.
//
// BACKEND DEPENDENCY (PE-1628 Stage 4/5): the `checkout.create` procedure and
// the fee-mode write are still being built on backends/pricing. Until they
// deploy, `isPricingBackendEnabled()` is false in any env where the URL env var
// is unset — so a caller never reaches a dead backend in prod. (The
// `pocketflows_marketing_tab` flag that also gated the Billing & Plans page
// was removed at full rollout, 2026-08.) When Stage 4/5 land, reconcile the procedure
// names/shapes below with the deployed router (code wins).

import { createTRPCProxyClient, httpBatchLink } from '@trpc/client';
import posthog from 'posthog-js';

import { getAuthToken } from '../utils/auth';

// Type mirrors of @charitystack/schemas/http/pricing (checkout.ts + fee-mode.ts).
//
// WHY INLINED rather than imported: dashboard's tsconfig uses
// `moduleResolution: "node"` (legacy), which CANNOT resolve the subpath
// `exports` entry `@charitystack/schemas/http/pricing` — tsc errors TS2307
// ("could not be resolved under your current 'moduleResolution' setting").
// This is the same constraint marketing.ts documents and works around by
// inlining its merchant-account / mirror types. The shared Zod schema in
// `contracts/schemas/src/http/pricing/` remains the SOURCE OF TRUTH (the
// pricing backend validates against it); these are a thin FE-side type mirror.
// Keep them in lockstep — if the schema input/output shape changes, change
// these. (The proper fix is migrating the dashboard to `moduleResolution:
// "bundler"`, tracked separately; same root cause as the dashboard-assistant
// note in the monorepo CLAUDE.md.)
export type CheckoutPlan = 'BASE' | 'PLUS' | 'PRO';
export type BillingPeriod = 'monthly' | 'yearly';
export type CheckoutCreateInput = { plan: CheckoutPlan; billingPeriod: BillingPeriod };
export type CheckoutCreateOutput = { checkoutUrl: string };
// Embedded Checkout: same input as checkout.create. Output has TWO
// mutually-exclusive outcomes (first-completed-wins guard, PE-1643), mirroring
// checkoutCreateEmbeddedOutputSchema in @charitystack/schemas/http/pricing:
//   - { alreadySubscribed: true } — the org already has a live pricing
//     subscription; NO session was minted. The caller must NOT mount
//     <EmbeddedCheckout> and should treat the org as already subscribed.
//   - { alreadySubscribed: false, clientSecret } — a fresh session; clientSecret
//     is the opaque `cs_..._secret_...` token the FE mounts into
//     <EmbeddedCheckoutProvider>.
// clientSecret is present iff alreadySubscribed === false.
export type CheckoutCreateEmbeddedOutput = {
  alreadySubscribed: boolean;
  clientSecret?: string;
};
export type CoveredCommitmentSetInput = { coveredCommitment: boolean };
export type CoveredCommitmentSetOutput = { coveredCommitment: boolean };
// Portal flow: 'manage' (default — portal home) vs 'subscription_update' (deep-
// link to the plan-switch screen, B4) vs 'subscription_cancel' (deep-link to the
// cancel screen — the Starter card's "Downgrade to Starter", PE-1974; the portal
// config cancels at period end). Mirror of the schemas contract.
export type BillingPortalFlow = 'manage' | 'subscription_update' | 'subscription_cancel';
export type BillingPortalCreateInput = { flow?: BillingPortalFlow };
export type BillingPortalCreateOutput = { portalUrl: string };

// Type mirrors of subscriptionChangePlan{Input,Output}Schema (PE-1987).
// `kind` reports the semantics the BACKEND applied (it decides from live
// Stripe state): 'trial_preserved' = switched now, trial intact, $0 today;
// 'immediate' = switched now, prorated difference invoiced today;
// 'scheduled' = parked at period end (downgrade-shaped changes).
export type SubscriptionChangePlanInput = {
  plan: CheckoutPlan;
  billingPeriod: BillingPeriod;
  // PE-1987 (review): the semantics the confirm dialog SHOWED the user. The
  // backend re-derives the real semantics at execution time and rejects with
  // CONFLICT if they no longer match (live state moved while the dialog sat
  // open — e.g. a trial converting would turn "$0 today" into a prorated
  // charge). Optional: omitting it skips the check (deploy-skew safe).
  expectedKind?: 'trial_preserved' | 'immediate' | 'scheduled';
};
export type SubscriptionChangePlanOutput = {
  kind: 'trial_preserved' | 'immediate' | 'scheduled';
  plan: CheckoutPlan;
  billingPeriod: BillingPeriod;
  // ISO-8601 — when the target plan takes effect.
  effectiveAt: string;
  // Integer cents — the NEW recurring amount (not today's prorated charge).
  amountCents: number;
  // ISO-8601 trial end; non-null only on kind 'trial_preserved'.
  trialEnd: string | null;
};
export type SubscriptionReleaseScheduledChangeOutput = { released: boolean };
// Live subscription summary (C1). A discriminated union on `hasSubscription`,
// mirroring contracts/schemas/src/http/pricing/subscription.ts. The banner
// branches on it: false → plan-only fallback; true → enriched state.
export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete';
export type SubscriptionPaymentMethod = { brand: string; last4: string };
export type SubscriptionSummaryOutput =
  | { hasSubscription: false }
  | {
      hasSubscription: true;
      plan: CheckoutPlan | null;
      status: SubscriptionStatus;
      renewalDate: string | null;
      cancelAtPeriodEnd: boolean;
      amountCents: number | null;
      interval: 'month' | 'year' | null;
      paymentMethod: SubscriptionPaymentMethod | null;
      pastDue: boolean;
      // PE-1758 — a scheduled, not-yet-effective downgrade. `cancel` = drops to
      // Starter at period end (Stripe cancel_at_period_end); `downgrade` = a
      // scheduled move to a lower paid tier (Stripe SubscriptionSchedule). null
      // or absent = nothing scheduled. Optional so a pricing backend that hasn't
      // shipped the field yet still parses. Mirror of the schemas contract.
      scheduledChange?: {
        kind: 'cancel' | 'downgrade';
        effectiveAt: string;
        targetPlan: 'STARTER' | 'BASE' | 'PLUS' | 'PRO' | null;
      } | null;
    };

// SMS "messaging credits" usage (PE-1639). Mirror of
// getSmsCreditsOutputSchema in @charitystack/schemas/http/pricing. `used` and
// `included` are credit counts (1 SMS = 1, 1 MMS = 2); period bounds are Unix
// SECONDS (0 when the org is not metered). Credits-only — the estimated
// overage $ shown in MessagingCredits is derived FE-side from the shared
// SMS_OVERAGE_UNIT_AMOUNT_CENTS in @charitystack/entitlements (PE-1989
// follow-up), not returned by this API.
export type SmsCreditsOutput = {
  used: number;
  included: number;
  plan: string;
  periodStart: number;
  periodEnd: number;
};

const PRICING_TRPC_URL = import.meta.env.VITE_APP_PRICING_BACKEND_TRPC_URL || '';

function normalizeBaseUrl(url: string): string {
  return url.replace(/\/$/, '');
}

export function isPricingBackendEnabled(): boolean {
  return Boolean(PRICING_TRPC_URL);
}

// Keep this untyped so the dashboard package does not import backend-only
// router modules. See file header for rationale.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let cachedClient: any = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getPricingClient(): any {
  const url = normalizeBaseUrl(PRICING_TRPC_URL);
  if (!url) {
    throw new Error(
      'Pricing backend is not configured. Set VITE_APP_PRICING_BACKEND_TRPC_URL in the dashboard env (Amplify Console or .env-cmdrc.json).',
    );
  }

  if (!cachedClient) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cachedClient = createTRPCProxyClient<any>({
      links: [
        httpBatchLink({
          url,
          async headers() {
            const token = await getAuthToken();
            const posthogDistinctId = posthog?.get_distinct_id?.();
            const posthogSessionId = posthog?.get_session_id?.();
            return {
              // Send the Clerk token in BOTH headers: `Authorization` for
              // local/non-OAC paths, and `x-cs-authorization` which survives the
              // CloudFront OAC SigV4 signing that overwrites `Authorization` on
              // the way to the Lambda (PE-1719). The pricing clerk-auth
              // middleware prefers `x-cs-authorization`.
              ...(token
                ? { Authorization: token, 'x-cs-authorization': token }
                : {}),
              ...(posthogDistinctId
                ? { 'x-posthog-distinct-id': posthogDistinctId }
                : {}),
              ...(posthogSessionId
                ? { 'x-posthog-session-id': posthogSessionId }
                : {}),
            };
          },
          // Disable HTTP-level caching of GETs (same fix as marketing.ts /
          // dashboardAssistantHistory.ts). Checkout/fee-mode are mutations
          // (POST) so this is belt-and-suspenders, but it keeps the client
          // identical to the house pattern.
          fetch(input, init) {
            return fetch(input, { ...init, cache: 'no-store' });
          },
        }),
      ],
    });
  }

  return cachedClient;
}

// Start a Stripe Checkout Session for a paid-tier upgrade. The FE redirects the
// browser to the returned `checkoutUrl` (full-page navigation). STARTER and
// ENTERPRISE are rejected server-side (PE-1628 AC-7) and the UI must not call
// this for them — the eligibility boundary is the BASE/PLUS/PRO `plan` enum in
// the input schema.
export async function createCheckout(
  input: CheckoutCreateInput,
): Promise<CheckoutCreateOutput> {
  return getPricingClient().checkout.create.mutate(input);
}

// Start an EMBEDDED Checkout Session (in-dashboard purchase). Returns a
// client_secret the EmbeddedCheckoutModal mounts into Stripe's
// <EmbeddedCheckoutProvider> — the user pays without leaving the dashboard.
// Same eligibility boundary as createCheckout (BASE/PLUS/PRO only; STARTER/
// ENTERPRISE rejected server-side).
export async function createEmbeddedCheckout(
  input: CheckoutCreateInput,
): Promise<CheckoutCreateEmbeddedOutput> {
  return getPricingClient().checkout.createEmbedded.mutate(input);
}

// Read the org's live subscription summary for the billing banner (C1). A
// QUERY. Returns { hasSubscription: false } (not an error) for a Starter or
// legacy-only org. Callers tolerate absence and render the plan-only fallback.
export async function getSubscriptionSummary(): Promise<SubscriptionSummaryOutput> {
  return getPricingClient().subscription.summary.query({});
}

// Read the org's SMS "messaging credits" usage for the dashboard display
// (PE-1639). A QUERY. Returns used/included + the billing-period window; the org
// comes from the authenticated context. Non-metered orgs (Starter/Base,
// Enterprise) and metered orgs with no usage return zeros — never an error. The
// `used` count is 0 everywhere until metered SMS is enabled (PE-1627 dark).
export async function getSmsCredits(): Promise<SmsCreditsOutput> {
  return getPricingClient().usage.getSmsCredits.query({});
}

// Persist the org-level fee mode (Covered Commitment vs Standard). This is
// a REAL org-level state transition (path-scoped write of
// `billingDetails.coveredCommitment`), so callers MUST gate it behind a
// confirmation step — never fire it on a raw toggle. The transactions money
// path derives the donation fee from this field; a client-only toggle would
// silently desync that derivation.
export async function setCoveredCommitment(
  input: CoveredCommitmentSetInput,
): Promise<CoveredCommitmentSetOutput> {
  return getPricingClient().coveredCommitment.set.mutate(input);
}

// Open a Stripe Customer Portal session for the org and return its URL. The FE
// redirects the browser to `portalUrl` (full-page navigation). The org is taken
// from the authenticated context on the backend — the only arg is the optional
// `flow`: omitted/'manage' opens the portal home (payment method, cancel,
// invoices); 'subscription_update' deep-links to the plan-switch screen (B4).
// Rejects with a TRPC NOT_FOUND when the org has no resolvable Stripe customer
// (no pricing-created subscription); the caller surfaces that as "no active
// subscription to manage".
export async function createBillingPortalSession(
  input: BillingPortalCreateInput = {},
): Promise<BillingPortalCreateOutput> {
  return getPricingClient().billingPortal.create.mutate(input);
}

// PE-1987: the general in-app plan change — the FULL replacement for the
// portal's subscription-update flow (Stripe's portal categorically rejects our
// multi-item subscriptions, and it ended running trials). The backend picks
// the semantics from live state + direction and reports them via `kind`.
// Rejects NOT_FOUND (no pricing subscription), PRECONDITION_FAILED (past_due /
// unpaid — settle dunning first), BAD_REQUEST (stale/invalid target).
export async function changePlanSubscription(
  input: SubscriptionChangePlanInput,
): Promise<SubscriptionChangePlanOutput> {
  return getPricingClient().subscription.changePlan.mutate(input);
}

// PE-1987: "Keep current plan" — undo a pending scheduled (period-end) plan
// change. `released: false` = nothing was scheduled (benign no-op).
export async function releaseScheduledChange(): Promise<SubscriptionReleaseScheduledChangeOutput> {
  return getPricingClient().subscription.releaseScheduledChange.mutate({});
}

// Re-export the client getter for advanced callers that want the proxy
// directly. Untyped — see file header.
export { getPricingClient };
